import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API } from "../config";
import { Target, Loader2, TrendingUp, Calendar, Award, Share2, Check, ArrowRight } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";

const AIStrategyPage = () => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const [myDomain, setMyDomain] = useState("");
  const [competitor, setCompetitor] = useState("");
  const [keywords, setKeywords] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [limitInfo, setLimitInfo] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!myDomain.trim() || !competitor.trim()) {
      setError(t.strat_err_both || "Enter both domains");
      return;
    }
    setLoading(true);
    setError(null);
    setResult(null);
    setLimitInfo(null);
    try {
      const kwList = keywords
        .split(/[,;\n]+/)
        .map((k) => k.trim())
        .filter(Boolean)
        .slice(0, 6);
      const r = await axios.post(`${API}/strategy/generate`, {
        my_domain: myDomain.trim(),
        competitor_domain: competitor.trim(),
        goal_keywords: kwList,
        language: language || "en",
        user_email: user?.email || null,
      });
      setResult(r.data);
      setTimeout(() => {
        document.getElementById("strategy-result")?.scrollIntoView({ behavior: "smooth" });
      }, 200);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 402 && typeof detail === "object") {
        setLimitInfo(detail);
      } else {
        setError((typeof detail === "string" ? detail : null) || t.strat_err_generic || "Generation failed");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBuyCredit = async () => {
    if (!user?.email) {
      window.location.href = "/login";
      return;
    }
    try {
      const r = await axios.post(`${API}/payments/checkout`, {
        package_id: "strategy_credit",
        origin_url: window.location.origin,
        user_email: user.email,
        language: language || "en",
      });
      if (r.data?.url) window.location.href = r.data.url;
    } catch (e) {
      setError(e.response?.data?.detail || "Checkout failed");
    }
  };

  const handleShare = async () => {
    if (!result?.share_id) return;
    const url = `${window.location.origin}/strategy/${result.share_id}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      window.prompt("Copy link:", url);
    }
  };

  return (
    <div
      className="min-h-screen"
      style={{ background: "var(--color-bg)", color: "var(--color-text)" }}
      data-testid="ai-strategy-page"
    >
      {/* Sticky-top nav button */}
      <div
        className="sticky top-0 z-40 backdrop-blur border-b"
        style={{
          background: "color-mix(in srgb, var(--color-bg) 80%, transparent)",
          borderColor: "var(--color-border)",
        }}
      >
        <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/" className="font-bold text-xl" style={{ color: "var(--color-text)" }}>
            ← <span style={{ color: "var(--color-primary)" }}>TopRankPulse</span>
          </Link>
          <span
            className="text-[10px] font-mono uppercase tracking-widest"
            style={{ color: "var(--color-accent)" }}
          >
            {t.strat_nav_title || "AI Strategy Generator"}
          </span>
        </div>
      </div>

      <section className="max-w-5xl mx-auto px-6 lg:px-12 pt-16 pb-12">
        <div className="text-center mb-10">
          <div
            className="inline-flex items-center gap-2 px-3 py-1 mb-4 rounded-full"
            style={{
              background: "color-mix(in srgb, var(--color-primary) 15%, transparent)",
              color: "var(--color-primary)",
            }}
          >
            <Target className="w-3.5 h-3.5" />
            <span className="text-[10px] font-mono uppercase tracking-widest font-bold">
              {t.strat_badge || "PRO / Free first strategy"}
            </span>
          </div>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-black font-['Cabinet_Grotesk'] tracking-tighter leading-[0.95] mb-4"
            style={{ color: "var(--color-text)" }}
          >
            {t.strat_title_a || "How to"}{" "}
            <span
              className="bg-clip-text text-transparent"
              style={{
                backgroundImage:
                  "linear-gradient(90deg, var(--color-primary) 0%, var(--color-accent) 100%)",
              }}
            >
              {t.strat_title_b || "outrank your competitor"}
            </span>
            <br />
            {t.strat_title_c || "in 30 days"}
          </h1>
          <p className="text-lg max-w-2xl mx-auto" style={{ color: "var(--color-text-muted)" }}>
            {t.strat_subtitle || "PulseAI Engine™ generates a concrete 4-week action plan tailored to your domain vs. a specific competitor."}
          </p>
        </div>

        {/* Form */}
        <form
          onSubmit={handleSubmit}
          className="rounded-2xl border p-6 sm:p-8 space-y-4"
          style={{ background: "var(--color-surface)", borderColor: "var(--color-border)" }}
          data-testid="strategy-form"
        >
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider mb-1.5"
                style={{ color: "var(--color-text-muted)" }}
              >
                {t.strat_label_my || "Your domain"}
              </label>
              <input
                type="text"
                value={myDomain}
                onChange={(e) => setMyDomain(e.target.value)}
                placeholder={t.strat_placeholder_my || "yourdomain.com"}
                data-testid="strategy-my-domain"
                required
                className="w-full px-4 py-3 rounded-lg border text-sm focus:outline-none"
                style={{
                  background: "var(--color-surface-alt)",
                  borderColor: "var(--color-border)",
                  color: "var(--color-text)",
                }}
              />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider mb-1.5"
                style={{ color: "var(--color-text-muted)" }}
              >
                {t.strat_label_competitor || "Competitor"}
              </label>
              <input
                type="text"
                value={competitor}
                onChange={(e) => setCompetitor(e.target.value)}
                placeholder={t.strat_placeholder_competitor || "competitor.com"}
                data-testid="strategy-competitor"
                required
                className="w-full px-4 py-3 rounded-lg border text-sm focus:outline-none"
                style={{
                  background: "var(--color-surface-alt)",
                  borderColor: "var(--color-border)",
                  color: "var(--color-text)",
                }}
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider mb-1.5"
              style={{ color: "var(--color-text-muted)" }}
            >
              {t.strat_label_keywords || "Target keywords (optional)"}
            </label>
            <input
              type="text"
              value={keywords}
              onChange={(e) => setKeywords(e.target.value)}
              placeholder={t.strat_placeholder_keywords || "running shoes, nike shoes, adidas..."}
              data-testid="strategy-keywords"
              className="w-full px-4 py-3 rounded-lg border text-sm focus:outline-none"
              style={{
                background: "var(--color-surface-alt)",
                borderColor: "var(--color-border)",
                color: "var(--color-text)",
              }}
            />
            <p className="text-[11px] mt-1" style={{ color: "var(--color-text-muted)" }}>
              {t.strat_kw_hint || "Comma-separated. Max 6 keywords."}
            </p>
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="strategy-submit-btn"
            className="w-full px-6 py-4 font-bold text-base rounded-lg transition-transform hover:scale-[1.01] flex items-center justify-center gap-2"
            style={{ background: "var(--color-primary)", color: "var(--color-bg)" }}
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                {t.strat_loading || "AI analyzing... (~15–30s)"}
              </>
            ) : (
              <>
                <Target className="w-5 h-5" />
                {t.strat_submit || "Generate 30-day strategy"}
              </>
            )}
          </button>
          {error && (
            <p className="text-sm text-center" style={{ color: "#ef4444" }}>
              {error}
            </p>
          )}
          {limitInfo && (
            <div
              data-testid="strategy-limit-banner"
              className="rounded-lg border-2 p-5 space-y-3"
              style={{
                borderColor: "var(--color-primary)",
                background: "color-mix(in srgb, var(--color-primary) 8%, transparent)",
              }}
            >
              <p className="font-bold text-base" style={{ color: "var(--color-text)" }}>
                🔒 {limitInfo.message}
              </p>
              <div className="flex flex-wrap gap-2">
                <Link
                  to="/#pricing"
                  data-testid="strategy-limit-upgrade"
                  className="px-4 py-2 text-sm font-bold rounded-lg"
                  style={{ background: "var(--color-primary)", color: "var(--color-bg)" }}
                >
                  {t.strat_upgrade || "Upgrade to PRO"} →
                </Link>
                <button
                  type="button"
                  onClick={handleBuyCredit}
                  data-testid="strategy-buy-credit"
                  className="px-4 py-2 text-sm font-bold rounded-lg border"
                  style={{
                    borderColor: "var(--color-primary)",
                    color: "var(--color-primary)",
                    background: "transparent",
                  }}
                >
                  + Extra strategy · €9
                </button>
              </div>
            </div>
          )}
        </form>
      </section>

      {/* Result */}
      {result && (
        <section
          id="strategy-result"
          data-testid="strategy-result"
          className="max-w-5xl mx-auto px-6 lg:px-12 pb-24 space-y-8"
        >
          {/* Gap analysis */}
          <div
            className="rounded-2xl border p-6 sm:p-8"
            style={{ background: "var(--color-surface)", borderColor: "var(--color-border)" }}
          >
            <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
              <h2 className="text-2xl font-black" style={{ color: "var(--color-text)" }}>
                {t.strat_gap_title || "📊 Current Gap Analysis"}
              </h2>
              <button
                onClick={handleShare}
                data-testid="strategy-share-btn"
                className="px-3 py-1.5 text-xs font-bold rounded-lg flex items-center gap-1.5 transition-colors"
                style={{
                  background: copied ? "var(--color-primary)" : "var(--color-surface-alt)",
                  color: copied ? "var(--color-bg)" : "var(--color-text)",
                }}
              >
                {copied ? <Check className="w-3.5 h-3.5" /> : <Share2 className="w-3.5 h-3.5" />}
                {copied ? (t.strat_copied || "Copied!") : (t.strat_share || "Share")}
              </button>
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              {[
                [t.strat_gap_score || "SEO Score", result.gap_analysis?.my_score, result.gap_analysis?.competitor_score],
                [t.strat_gap_keywords || "Keywords in TOP 100", result.gap_analysis?.my_keywords_top100, result.gap_analysis?.competitor_keywords_top100],
                [t.strat_gap_ai_vis || "AI Visibility %", result.gap_analysis?.my_ai_visibility, result.gap_analysis?.competitor_ai_visibility],
              ].map(([label, me, them]) => (
                <div
                  key={label}
                  className="p-4 rounded-lg border"
                  style={{ background: "var(--color-surface-alt)", borderColor: "var(--color-border)" }}
                >
                  <p className="text-[10px] font-mono uppercase tracking-widest mb-2"
                    style={{ color: "var(--color-text-muted)" }}
                  >
                    {label}
                  </p>
                  <div className="flex items-end gap-2">
                    <div className="flex-1">
                      <p className="text-[10px]" style={{ color: "var(--color-text-muted)" }}>{t.strat_you || "You"}</p>
                      <p className="text-2xl font-black" style={{ color: "var(--color-primary)" }}>
                        {me}
                      </p>
                    </div>
                    <div className="flex-1">
                      <p className="text-[10px]" style={{ color: "var(--color-text-muted)" }}>{t.strat_competitor || "Competitor"}</p>
                      <p className="text-2xl font-black" style={{ color: "var(--color-text)" }}>
                        {them}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Unfair advantages */}
          {result.unfair_advantages?.length > 0 && (
            <div
              className="rounded-2xl border p-6 sm:p-8"
              style={{ background: "var(--color-surface)", borderColor: "var(--color-border)" }}
            >
              <h2 className="text-2xl font-black mb-4" style={{ color: "var(--color-text)" }}>
                {t.strat_advantages_title || "🔥 Your unfair advantages"}
              </h2>
              <ul className="space-y-2.5">
                {result.unfair_advantages.map((adv, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span
                      className="w-7 h-7 rounded-lg font-black flex items-center justify-center shrink-0 text-sm"
                      style={{
                        background: "var(--color-primary)",
                        color: "var(--color-bg)",
                      }}
                    >
                      {i + 1}
                    </span>
                    <span style={{ color: "var(--color-text)" }}>{adv}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Weeks */}
          <div className="space-y-4">
            {(result.weeks || []).map((w, widx) => (
              <div
                key={widx}
                data-testid={`strategy-week-${w.number}`}
                className="rounded-2xl border p-6 sm:p-8"
                style={{ background: "var(--color-surface)", borderColor: "var(--color-border)" }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <Calendar className="w-5 h-5" style={{ color: "var(--color-accent)" }} />
                  <span
                    className="text-[10px] font-mono uppercase tracking-widest"
                    style={{ color: "var(--color-text-muted)" }}
                  >
                    {t.strat_week_label || "Week"} {w.number}
                  </span>
                  <h3 className="text-lg sm:text-xl font-black" style={{ color: "var(--color-text)" }}>
                    {w.title}
                  </h3>
                </div>
                <ul className="space-y-2">
                  {(w.tasks || []).map((t, ti) => (
                    <li
                      key={ti}
                      className="flex items-start gap-3 p-3 rounded-lg"
                      style={{ background: "var(--color-surface-alt)" }}
                    >
                      <ArrowRight
                        className="w-4 h-4 shrink-0 mt-0.5"
                        style={{ color: "var(--color-primary)" }}
                      />
                      <div>
                        <p
                          className="text-[10px] font-mono uppercase tracking-widest mb-0.5"
                          style={{ color: "var(--color-text-muted)" }}
                        >
                          {t.day_range}
                        </p>
                        <p className="text-sm" style={{ color: "var(--color-text)" }}>
                          {t.action}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Forecast */}
          {result.forecast && (
            <div
              className="rounded-2xl border p-6 sm:p-8"
              style={{
                background: "linear-gradient(135deg, color-mix(in srgb, var(--color-primary) 20%, transparent), color-mix(in srgb, var(--color-accent) 20%, transparent))",
                borderColor: "var(--color-primary)",
              }}
            >
              <div className="flex items-center gap-2 mb-4">
                <Award className="w-6 h-6" style={{ color: "var(--color-primary)" }} />
                <h2 className="text-2xl font-black" style={{ color: "var(--color-text)" }}>
                  {t.strat_forecast_title || "🎯 Forecasted result (30 days)"}
                </h2>
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                {[
                  [t.strat_forecast_traffic || "Organic traffic", result.forecast.organic_traffic_delta, <TrendingUp key="a" className="w-5 h-5" />],
                  [t.strat_forecast_position || "Target position", result.forecast.target_position, "🎯"],
                  [t.strat_forecast_ai_vis || "AI Visibility", result.forecast.ai_visibility_delta, "🤖"],
                ].map(([label, val, icon], i) => (
                  <div key={i} className="text-center">
                    <div className="text-3xl mb-1">{icon}</div>
                    <p className="text-[10px] font-mono uppercase tracking-widest"
                      style={{ color: "var(--color-text-muted)" }}
                    >
                      {label}
                    </p>
                    <p className="text-2xl font-black" style={{ color: "var(--color-text)" }}>
                      {val}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* PRO hook */}
          {result.pro_hook && (
            <div
              className="rounded-2xl p-6 sm:p-8 text-center border-2"
              style={{
                background: "var(--color-surface-alt)",
                borderColor: "var(--color-primary)",
              }}
            >
              <p className="text-lg mb-4" style={{ color: "var(--color-text)" }}>
                💡 {result.pro_hook}
              </p>
              <Link
                to="/#pricing"
                className="inline-flex items-center gap-2 px-6 py-3 font-bold rounded-lg transition-transform hover:scale-105"
                style={{ background: "var(--color-primary)", color: "var(--color-bg)" }}
              >
                {t.strat_upgrade || "Upgrade to PRO"}
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          )}
        </section>
      )}
    </div>
  );
};

export default AIStrategyPage;
